import type { LevelBand } from '@/shared/levels'
import { PLAYER_POSITIONS, type PlayerPosition } from '@/core/models/user'

// Pure mapping from the onboarding form to the `users/{uid}` write payload —
// extracted so the band→nivel, position-label→enum, and phone handling are
// unit-testable without rendering the page.

const POSITION_BY_LABEL: Record<string, PlayerPosition> = {
  Derecha: 'drive',
  Revés: 'reverse',
  Indiferente: 'both',
}

export interface OnboardingInput {
  name: string
  city: string
  phone: string
  band: LevelBand
  positionLabel: string
  /** Present when the phone arrived via the SMS-verified login flow. */
  phoneVerified: boolean
  /** Optional self-declared gender — needed only to join restricted matches. */
  genderLabel: 'Mujer' | 'Hombre' | null
  /** Feed anchor (research F8) — usually the club that distributed the app. */
  homeClubId: string | null
  email: string | null
  photoURL: string | null
  registrationMethod: 'email_password' | 'google_signin' | 'phone' | null
}

export function buildOnboardingUserDoc(input: OnboardingInput): Record<string, unknown> {
  const position = POSITION_BY_LABEL[input.positionLabel] ?? null
  if (position && !PLAYER_POSITIONS.includes(position)) throw new Error('unreachable')
  const phone = input.phone.trim()
  return {
    profile: {
      name: input.name.trim(),
      surnames: null,
      email: input.email,
      photoURL: input.photoURL,
    },
    padel: {
      // Band chip → the band's default value; provenance is always 'self' at
      // onboarding (decision 2026-07-04).
      skillLevel: input.band.default,
      levelSource: 'self',
      position,
      // Optional; gates joining Femenino / Masculino / Mixto matches.
      gender: input.genderLabel === 'Mujer' ? 'female' : input.genderLabel === 'Hombre' ? 'male' : null,
    },
    phone: {
      phoneNumber: phone || null,
      // The rules let a client CLEAR this but never assert it, so a `true` here
      // is always rejected — the flag is a server fact. `phoneVerified` is
      // hardcoded false at the only call site, so this branch is unreachable
      // today; it stays as the shape the OTP work will fill in.
      isPhoneValidated: input.phoneVerified && phone.length > 0,
      // `phoneValidatedAt` is deliberately NOT written here. It used to be
      // stamped with `new Date()` — a CLIENT clock deciding when a server fact
      // happened, on a device whose time the user controls. When real OTP lands
      // (roadmap #21) the Cloud Function stamps it with serverTimestamp via the
      // admin SDK, alongside the flag it belongs to. This builder sits outside
      // the `api/` boundary and cannot import the Firestore SDK anyway.
    },
    homeClubId: input.homeClubId,
    location: {
      city: input.city.trim() || null,
    },
    status: {
      registrationMethod: input.registrationMethod,
    },
  }
}
